import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, EstimateButton } from "@/components/Shared";
import { PROJECTS, PROJECT_FILTERS } from "@/data/content";
import { useSEO } from "@/hooks/useSEO";

export default function Projects() {
  useSEO(
    "Renovation Projects & Portfolio | MTC Home Renovation GTA",
    "Browse recent kitchen, bathroom, basement, flooring and whole-home renovation projects by MTC Home Renovation across the Greater Toronto Area."
  );
  const [filter, setFilter] = useState("All");
  const visible = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <>
      <section className="py-16 sm:py-24 border-b border-obsidian/10" data-testid="projects-header">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-stonemuted">Projects · Portfolio</p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.1] text-obsidian max-w-2xl">
              Recent renovations across the GTA.
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-stone-600 font-light leading-relaxed">
              A selection of our kitchen, bathroom, basement and whole-home work.
            </p>
          </Reveal>
          <div className="mt-10 flex flex-wrap gap-2" data-testid="project-filters">
            {PROJECT_FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`px-4 py-2.5 text-sm font-medium border transition-colors duration-300 ${
                  filter === f ? "bg-obsidian text-bone border-obsidian" : "border-obsidian/20 text-obsidian/70 hover:border-obsidian/50"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20" data-testid="projects-grid">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.div
                layout
                key={p.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`project-card-${p.id}`}
                className="group"
              >
                <div className="overflow-hidden border border-obsidian/15 aspect-[4/3]">
                  <img
                    src={p.image}
                    alt={`${p.title} — ${p.category} renovation in ${p.area}, GTA`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-teak">{p.category} · {p.area}</p>
                <h2 className="mt-1 font-display text-xl font-medium text-obsidian">{p.title}</h2>
                <p className="mt-1.5 text-sm text-stone-600 leading-relaxed">{p.blurb}</p>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </section>

      <section className="bg-parchment/60 border-t border-obsidian/10 py-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <h2 className="font-display text-2xl sm:text-3xl font-medium text-obsidian">Your home could be next.</h2>
          <EstimateButton testId="projects-estimate-button" />
        </div>
      </section>
    </>
  );
}
